import React, { useRef } from 'react';

// ============================================================================
// ColorSwatches — the form-controls standard for assigning a color (crew
// display color, truck color, status color). A curated grid that reads on
// the dispatch board, one tap; "Custom…" opens the OS picker for the rare
// exact-company-color case (Jaylen 2026-09-11: "presented with a bunch of
// colors that would not conflict… also access the native color picker if
// you wanted to"). (form-controls.md "Color: A")
//
// - `taken`: hexes already used by OTHER crew/trucks. Those swatches show
//   a small dot and a "used by…" title so no two leads end up nearly the
//   same blue; they're still pickable (the board tolerates it, and there
//   are more leads than palette colors some days).
// - A `value` that isn't in the palette (every existing crew member —
//   their colors are NOT to be changed) is shown as an extra "current"
//   swatch, selected, so opening the form never silently reassigns
//   anyone.
// - Hidden <input type="color"> is the single sanctioned native control
//   in the app; it only opens from the Custom… button.
//
// Props: value (hex), onChange(hex), colors (default PALETTE), taken
// ([hex] or {hex: 'label'}), size ('md' 36px | 'sm' 28px), custom
// (default true), ariaLabel, className.
// ============================================================================

// 20 colors, all distinct from each other at pill size on a white board.
export const PALETTE = [
    '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d',
    '#16a34a', '#059669', '#0d9488', '#0891b2', '#0284c7',
    '#2563eb', '#4f46e5', '#7c3aed', '#9333ea', '#c026d3',
    '#db2777', '#e11d48', '#475569', '#78716c', '#1f2937',
];

const normHex = (c) => (typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c.trim()) ? c.trim().toLowerCase() : null);

export default function ColorSwatches({ value, onChange, colors = PALETTE, taken = [], size = 'md', custom = true, ariaLabel = 'Color', className = '' }) {
    const nativeRef = useRef(null);
    const cur = normHex(value);
    const takenMap = Array.isArray(taken)
        ? Object.fromEntries(taken.map(t => [normHex(t), 'another crew member']).filter(([k]) => k))
        : Object.fromEntries(Object.entries(taken).map(([k, v]) => [normHex(k), v]).filter(([k]) => k));
    const palette = colors.map(normHex).filter(Boolean);
    const extra = cur && !palette.includes(cur) ? [cur] : [];
    const all = [...extra, ...palette];
    const dim = size === 'sm' ? 'w-7 h-7' : 'w-9 h-9';

    return (
        <div role="radiogroup" aria-label={ariaLabel} className={`flex flex-wrap items-center gap-1.5 ${className}`}>
            {all.map(c => {
                const on = c === cur;
                const usedBy = takenMap[c];
                const isExtra = extra.includes(c);
                return (
                    <button
                        key={c}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        aria-label={isExtra ? `Current color ${c}` : c}
                        title={isExtra ? `Current color ${c}` : usedBy ? `${c} — used by ${usedBy}` : c}
                        onClick={() => onChange?.(c)}
                        style={{ background: c }}
                        className={`relative ${dim} rounded-lg transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 ${
                            on ? 'ring-2 ring-offset-2 ring-slate-900 scale-105' : 'hover:scale-110'
                        }`}
                    >
                        {on && <span aria-hidden="true" className="absolute inset-0 grid place-items-center text-white font-black text-[14px] drop-shadow">✓</span>}
                        {usedBy && !on && <span aria-hidden="true" className="absolute right-0.5 bottom-0.5 w-2 h-2 rounded-full bg-white/90 shadow" />}
                        {isExtra && <span aria-hidden="true" className="absolute -top-1 -left-1 text-[9px] font-bold bg-white text-slate-700 rounded px-0.5 shadow">now</span>}
                    </button>
                );
            })}
            {custom && (
                <>
                    <button
                        type="button"
                        onClick={() => nativeRef.current?.click()}
                        title="Open the OS color wheel"
                        className={`${size === 'sm' ? 'h-7 px-2 text-[11px]' : 'h-9 px-3 text-[12px]'} rounded-lg border border-dashed border-slate-300 text-slate-600 font-semibold hover:border-slate-400 hover:text-slate-900 inline-flex items-center gap-1.5`}
                    >
                        <span aria-hidden="true" className="w-3.5 h-3.5 rounded-full" style={{ background: 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)' }} />
                        Custom…
                    </button>
                    {/* Sanctioned exception: the OS picker behind Custom… (form-controls.md) */}
                    <input
                        ref={nativeRef}
                        type="color"
                        tabIndex={-1}
                        aria-hidden="true"
                        value={cur || '#2563eb'}
                        onChange={e => onChange?.(e.target.value)}
                        className="sr-only"
                    />
                </>
            )}
        </div>
    );
}
